import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Pagination } from '@/components/common/Pagination';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { useAuth } from '@/features/auth';
import { useSettingsFilters } from '../hooks';
import { activityTypesApi, settingsQueryKeys } from '../api';
import { SettingsDataTable, SettingsAction, useSettingsColumns } from '../components';
import type { ActivityType } from '../types';

export function ActivityTypesPage() {
  const { t } = useTranslation();
  const { hasAccess } = useAuth();
  const { filters, updateFilter, setPage, setLimit, handleSort } = useSettingsFilters();

  // Zoom is owned here so its control can sit in the Pagination row (left of
  // the per-page select) while the table applies the same value to its rows.
  const tableZoom = useTableZoom();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: settingsQueryKeys.activityTypes.list(filters),
    queryFn: () => activityTypesApi.getAll(filters),
  });

  const columns = useSettingsColumns<ActivityType>({
    orderBy: filters.orderBy,
    order: filters.order,
    onSort: handleSort,
    resource: 'activity-type',
    apiEndpoint: 'Activity-Types',
    queryKey: 'activityTypes',
    title: t('activityType'),
  });

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b">
        <h1 className="text-xl font-semibold">{t('activityType')}</h1>
        <div className="flex items-center gap-3">
          {hasAccess('activity-type', 'create') && (
            <SettingsAction
              title={t('activityType')}
              apiEndpoint="Activity-Types"
              queryKey="activityTypes"
              placeholder={t('enterActivityTypeName') || t('enterName')}
              successMessageCreate={t('activityTypeCreated')}
              successMessageUpdate={t('activityTypeUpdated')}
            />
          )}
        </div>
      </div>

      <div className="px-4 pt-4">
        <SettingsDataTable
          tableZoom={tableZoom}
          tableId="activity-types"
          data={data?.results || []}
          columns={columns}
          filters={filters}
          updateFilter={updateFilter}
          isLoading={isLoading}
          isError={isError}
          error={error?.message}
          emptyMessage={t('noActivityTypes')}
          createButton={
            hasAccess('activity-type', 'create') ? (
              <SettingsAction
                title={t('activityType')}
                apiEndpoint="Activity-Types"
                queryKey="activityTypes"
              />
            ) : undefined
          }
        />

        <Pagination
          leadingControls={<TableZoomControls zoom={tableZoom} />}
          page={filters.page}
          limit={filters.limit}
          total={data?.meta?.total ?? 0}
          onPageChange={setPage}
          onLimitChange={setLimit}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
